import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { NS as U340X_KPI_NS } from '../u340xKpiShared/u340xKpiNs';
import { doInitLU, luHandleAppiError, type U3400InitLU } from './U3400Service';
import { U3400ContextProvider } from './U3400Context';

export const NS = 'u3400';

export const U3400ROLES = {
  READ: 'U3400:READ',
} as const;
export type U3400ROLES = (typeof U3400ROLES)[keyof typeof U3400ROLES];

const defaultInitLU: U3400InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 50,
  catalogueTiles: [],
  currentLayout: {
    id: null,
    version: null,
    tiles: [],
  },
  maxTiles: 3,
  monthPreferences: [],
};

const U3400: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3400InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces([NS, U340X_KPI_NS], (err) => {
      if (err) {
        console.error('Error loading namespace:', err);
        return;
      }
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3400InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3400InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        catalogueTiles: initLU.catalogueTiles ?? [],
        currentLayout: {
          ...defaultInitLU.currentLayout,
          ...(initLU.currentLayout ?? {}),
          tiles: initLU.currentLayout?.tiles ?? [],
        },
        monthPreferences: initLU.monthPreferences ?? [],
        maxTiles: initLU.maxTiles ?? defaultInitLU.maxTiles,
      });
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(
        error as Error,
        t,
        NS,
        luHandleAppiError,
      ) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t('screenTitle', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, setLogicalUnitTitle, setLogicalUnitBreadcrumb, t]);

  if (!i18nReady || !initLUReady) {
    return null;
  }

  return (
    <U3400ContextProvider initLU={initLUData}>
      <Outlet />
    </U3400ContextProvider>
  );
};

export default U3400;
